import type {GetSyncPromise, SyncPromise} from '@gaia/utils/react/useGetSyncPromise';
import type {LineString} from '@turf/helpers';
import every from 'lodash/every';
import find from 'lodash/find';

import {getTrackLineRequests, type GetTrackLineRequestsOptions} from './getTrackLineRequests';

export interface SyncGetTrackLinesOptions extends GetTrackLineRequestsOptions {
    getSyncPromise: GetSyncPromise;
}

export function syncGetTrackLines({
    getSyncPromise,
    ...options
}: SyncGetTrackLinesOptions): SyncPromise<TrackLine[]> {
    const syncPromises = getTrackLineRequests(options).map((request): SyncPromise<TrackLine> => {
        const [geometry, error, isGeometryLoaded] =
            request.type === 'sync'
                ? [request.geometry, undefined, true]
                : getSyncPromise(request.geometry, request.loadingGeometry);

        return [
            {
                type: 'Feature',
                properties: {
                    ...options.track.properties,
                    ...request.properties,
                    isGeometryLoaded,
                },
                geometry,
            },
            error,
            isGeometryLoaded,
        ];
    });

    return [
        syncPromises.map(([line]) => line),
        find(syncPromises.map(([, error]) => error)),
        every(syncPromises.map(([, , isLoaded]) => isLoaded)),
    ];
}

export async function asyncGetTrackLines(
    options: GetTrackLineRequestsOptions
): Promise<TrackLine[]> {
    const trackLineRequests = getTrackLineRequests(options);

    type IntermediateResult = {
        geometry: LineString;
        properties: TrackLineRequestProperties;
        isGeometryLoaded: boolean;
        error: Error | undefined;
    };

    const trackLinePromises: Array<Promise<IntermediateResult>> = trackLineRequests.map(
        (request) => {
            if (request.type === 'sync') {
                return Promise.resolve({
                    geometry: request.geometry,
                    properties: request.properties,
                    isGeometryLoaded: true,
                    error: undefined,
                });
            } else {
                const {geometry: geometryPromise, loadingGeometry, properties} = request;

                return geometryPromise
                    .then((resolvedGeometry) => ({
                        geometry: resolvedGeometry,
                        properties,
                        isGeometryLoaded: true,
                        error: undefined,
                    }))
                    .catch((error) => ({
                        geometry: loadingGeometry,
                        properties,
                        isGeometryLoaded: false,
                        error,
                    }));
            }
        }
    );

    const results: IntermediateResult[] = await Promise.all(trackLinePromises);

    return results.map(({geometry, properties, isGeometryLoaded, error}) => ({
        type: 'Feature',
        properties: {
            ...options.track.properties,
            ...properties,
            isGeometryLoaded,
            error,
        },
        geometry,
    }));
}
